import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AuthHTTPService } from 'src/app/_theme/core/users/Auth/_services/auth-http/auth-http.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { UserstorageService } from 'src/app/_theme/core/users/userstorage/userstorage.service';
import { isValidIanaTimeZone } from '../shared/models/status-time.utils';

@Injectable({ providedIn: 'root' })
export class StatusPageProfileTimeZoneService {
  private profileTimeZone$?: Observable<string | null>;

  constructor(
    private readonly authService: AuthService,
    private readonly authHttpService: AuthHTTPService,
    private readonly userStorageService: UserstorageService
  ) {}

  getProfileTimeZone(): Observable<string | null> {
    if (!this.profileTimeZone$) {
      this.profileTimeZone$ = this.resolveProfileTimeZone().pipe(
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.profileTimeZone$;
  }

  private resolveProfileTimeZone(): Observable<string | null> {
    const token = this.authService.getToken();
    const userId = this.getCurrentUserId();
    if (!token || !userId) {
      return of(null);
    }

    return this.authService.getUserById(token, userId).pipe(
      take(1),
      switchMap((profile: any) => {
        const profileTimeZoneName = String(profile?.time_zone || profile?.timeZone || '').trim();
        return this.resolveTimeZoneName(profileTimeZoneName);
      }),
      catchError(() => of(null))
    );
  }

  private resolveTimeZoneName(value: string): Observable<string | null> {
    if (!value) {
      return of(null);
    }

    if (isValidIanaTimeZone(value)) {
      return of(value);
    }

    return this.authHttpService.getTimeZoneList().pipe(
      take(1),
      map((timeZones: any) => {
        const mappedZone = Array.isArray(timeZones)
          ? timeZones.find((item: any) => item?.name === value)?.zone
          : null;
        return isValidIanaTimeZone(mappedZone) ? mappedZone : null;
      }),
      catchError(() => of(null))
    );
  }

  private getCurrentUserId(): string {
    const stored = this.userStorageService.getuserstorage();
    if (!stored) {
      return '';
    }

    try {
      const parsed = JSON.parse(stored);
      return String(parsed?.user_id || parsed?.userId || '').trim();
    } catch {
      return '';
    }
  }
}
